import { FC, useState } from 'react';

import { formatMoney } from '@betterment/core';
import { Box, Button, Dialog, DropdownMenu, IconButton, Sheet, Table, Tag, Text } from '@betterment/samba-react';
import { useFeatureFlag } from '@betterment/shared-components';
import { nanoid } from 'nanoid';
import { Link } from 'react-router-dom';

import BillingPlanTableBodyRowCell from './BillingPlanTableBodyRowCell';
import { BillingPlanPageDrawer } from './components/drawer/BillingPlanPageDrawer';
import { NumberOfClients } from './components/NumberOfClients';

import { BillingPlan, PlatformFeePaidBy } from '../../../../graphql/schema';
import urlBuilder from '../../../../Shared/url-builder';
import {
  BillingConfigurationTypeMap,
  BillingRateType,
  PaymentTypeMap,
  TIERED_CALCULATION_LABEL,
  isTieredBillingRate,
} from '../data';
import SetDefaultModal from '../SetDefaultModal';
import ViewTieredRateModal from '../ViewTieredRateModal';

const PlatformFeeText: FC<{ plan: BillingPlan; platformFee: number }> = ({ plan, platformFee }) => {
  const { platformFeeAllocation } = plan;
  if (platformFeeAllocation.__typename === 'SharedPlatformFeeAllocation') {
    const { clientPortion } = platformFeeAllocation;
    return (
      <Box css={{ display: 'flex', flexDirection: 'column' }}>
        <Text>Shared</Text>
        <Text color="text.weak">
          Client {clientPortion} bps, your firm {Math.max(platformFee - clientPortion, 0)} bps
        </Text>
      </Box>
    );
  }

  switch (platformFeeAllocation.paidBy) {
    case PlatformFeePaidBy.ClientOnly:
      return <Text>Client</Text>;
    case PlatformFeePaidBy.AdvisorOnly:
      return <Text>Your firm</Text>;
    default:
      return <Text>N/A</Text>;
  }
};

const BillingPlanIndexPageRow: FC<{
  plan: BillingPlan;
  isLastIndex: boolean;
  defaultBillingPlan: BillingPlan;
  platformFee: number;
}> = ({ plan, isLastIndex, defaultBillingPlan, platformFee }) => {
  const [isDefaultModalIsOpen, setDefaultModalIsOpen] = useState(false);
  const [isTieredBillingModalOpen, setIsTieredBillingModalOpen] = useState<boolean>(false);

  const { enabled: isNettingEnabled, loading: isNettingEnabledLoading } = useFeatureFlag({
    splitName: 'retail.b4a_platform_fee_netting_q3_2025_enabled',
    context: 'BillingPlanIndexPage',
  });

  const { enabled: isCliffBasedEnabled } = useFeatureFlag({
    splitName: 'retail.b4a_cliff_based_billing_q1_2026_enabled',
    context: 'BillingPlanIndexPage',
  });
  const shouldShowNetting = isNettingEnabled && !isNettingEnabledLoading;

  const getRate = (billingPlan: BillingPlan) => {
    const { billingRate } = billingPlan;
    switch (billingRate.__typename) {
      case BillingRateType.AssetBased:
        return `${billingRate.feeBps} bps`;
      case BillingRateType.Fixed:
        return formatMoney(billingRate.feeCents);
      case BillingRateType.Tiered:
        return (
          <Dialog open={isTieredBillingModalOpen} onOpenChange={setIsTieredBillingModalOpen} overlay="default">
            <Dialog.Trigger asChild={true}>
              <Button variant="textPrimary">{shouldShowNetting ? 'Tiered' : 'View'}</Button>
            </Dialog.Trigger>
            <ViewTieredRateModal plan={plan} platformFee={platformFee} />
          </Dialog>
        );
      default:
        return 'N/A';
    }
  };

  return (
    <Table.Row aria-label="billingPlanRow">
      <Sheet overlay="weak">
        <BillingPlanTableBodyRowCell planName={plan.name} isLastIndex={isLastIndex}>
          <Box css={{ display: 'inline-flex', alignItems: 'center', maxWidth: '40rem' }}>
            <Text
              aria-label="billingPlanName"
              css={{
                display: 'inline-block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                paddingRight: 'space.50',
              }}
            >
              {plan.name}
            </Text>
            {plan.firmDefault && <Tag accent="blue">Firm default</Tag>}
          </Box>
          <Text as="p" variant="text50" color="text.weak">
            {plan.latestChangeDescription}
          </Text>
        </BillingPlanTableBodyRowCell>
        {!shouldShowNetting && (
          <BillingPlanTableBodyRowCell isLastIndex={isLastIndex}>
            <Text as="p">{BillingConfigurationTypeMap[plan.billingRate.__typename]}</Text>
          </BillingPlanTableBodyRowCell>
        )}
        <BillingPlanTableBodyRowCell isLastIndex={isLastIndex}>
          <Box css={{ display: 'flex', flexDirection: 'column', maxWidth: '14rem' }}>
            <Text aria-label="billingPlanRate">{getRate(plan)}</Text>
            {isTieredBillingRate(plan.billingRate) && isCliffBasedEnabled && (
              <Text as="p" variant="text50" color="text.weak">
                {TIERED_CALCULATION_LABEL[plan.billingRate.calculationMethod]}
              </Text>
            )}
          </Box>
        </BillingPlanTableBodyRowCell>
        <BillingPlanTableBodyRowCell isLastIndex={isLastIndex}>
          <Box css={{ maxWidth: '17rem' }}>
            <Text aria-label="billingPlanFeeAssessmentSource">{PaymentTypeMap[plan.paymentType]}</Text>
          </Box>
        </BillingPlanTableBodyRowCell>
        <BillingPlanTableBodyRowCell isLastIndex={isLastIndex}>
          <Box css={{ maxWidth: '20rem' }}>
            <NumberOfClients numClients={plan.numberOfClients} />
          </Box>
          <Sheet.Content css={{ overflowY: 'auto', gap: 'space.300' }} aria-describedby={undefined}>
            <BillingPlanPageDrawer planName={plan.name} numberOfClients={plan.numberOfClients} planId={plan.id} />
          </Sheet.Content>
        </BillingPlanTableBodyRowCell>
        {shouldShowNetting && (
          <BillingPlanTableBodyRowCell isLastIndex={isLastIndex}>
            <Box css={{ maxWidth: '24rem' }}>
              <PlatformFeeText plan={plan} platformFee={platformFee} />
            </Box>
          </BillingPlanTableBodyRowCell>
        )}
        <BillingPlanTableBodyRowCell isLastIndex={isLastIndex}>
          <Box css={{ display: 'flex', justifyContent: 'end', float: 'right', maxWidth: '5rem' }}>
            <DropdownMenu aria-label="billingPlanOptions">
              <DropdownMenu.Trigger asChild={true}>
                <IconButton name="Ellipses" label="dropdownButton" spacing="compact" />
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                <Link to={urlBuilder.getEditBillingPlanFlowPage({ billingPlanId: plan.id, id: nanoid() })}>
                  <DropdownMenu.Item aria-label="billingPlanEditButton">Edit</DropdownMenu.Item>
                </Link>
                {!plan.firmDefault && (
                  <DropdownMenu.Item
                    aria-label="billingPlanSetDefaultButton"
                    onSelect={() => setDefaultModalIsOpen(true)}
                  >
                    Set as default
                  </DropdownMenu.Item>
                )}
              </DropdownMenu.Content>
            </DropdownMenu>
            <SetDefaultModal
              isOpen={isDefaultModalIsOpen}
              closeHandler={() => setDefaultModalIsOpen(false)}
              billingPlan={plan}
              defaultBillingPlan={defaultBillingPlan}
            />
          </Box>
        </BillingPlanTableBodyRowCell>
      </Sheet>
    </Table.Row>
  );
};

export default BillingPlanIndexPageRow;
